<template>
  <div layout="column">
    <div layout="row">
      <div class="title mb-3">
        Interns evaluations
      </div>
    </div>
    <div v-if="loaded">
      <div
        v-if="hasNoForm"
        class="alert"
      >
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span>No intern's form has been found for this type/concept of session. Please contact an administrator.</span>
      </div>
      <div v-if="isTrainer || isAdminEditingEval">
        <div v-if=" ! currentEval && isAdminEditingEval">
          <div class="mb-3">
            <strong>Who do you want to create the evaluation for ?</strong>
          </div>
          <div v-if="remainingInterns.length > 0">
            <div id="create-as-admin">
              <v-select
                v-model="evalToBeCreatedForInternID"
                :items="remainingInterns"
                item-text="fullname"
                item-value="id"
                label="Choose an intern"
                outline
                :hide-details="true"
              />
              <div class="save-section">
                <v-btn
                  depressed
                  color="primary"
                  @click="createEval"
                >
                  Create
                </v-btn>
                <span>ou&nbsp;&nbsp;<a @click="cancelCreation">Cancel</a></span>
              </div>
            </div>
          </div>
          <div v-else>
            <em>All evaluations for this session have already been created, no intern is missing. If you need to add a new intern evaluation, you have to add this intern to the session first</em>
            <div>
              <v-btn
                depressed
                color="primary"
                @click="cancelCreation"
              >
                Back to the list
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else-if="currentEval">
          <div
            v-if="isAdminEditingEval"
            class="legend info-legend mb-4"
          >
            You are editing the evaluation of <strong>{{ currentEval.intern.firstname }} {{ currentEval.intern.lastname }}</strong>
          </div>
          <v-form
            ref="form"
            v-model="formValid"
            @submit.stop.prevent="save"
          >
            <div class="main-container">
              <div class="form-container">
                <generic-form
                  :key="`${currentEval.id}-${currentEval.updated_at}`"
                  :evaluation="currentEval"
                  :readonly="isReadonly"
                  @change="updateRapport"
                />
              </div>
              <div class="meta-container">
                <div class="meta">
                  <div class="save-section mb-3">
                    <v-btn
                      depressed
                      color="primary"
                      :disabled="isReadonly"
                      @click="save"
                    >
                      Save form
                    </v-btn>
                    <div v-if="isAdminEditingEval">
                      <span>ou&nbsp;&nbsp;<a @click="cancelCreation">Cancel</a></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </div>
      </div>
      <div
        v-else
        layout-gt-md="row"
        layout-align="start stretch"
        layout-wrap
        style="width: 100%"
      >
        <generic-evals
          v-slot="{evaluation}"
          :evaluations="evaluationsByTime"
          @edit="(e) => adminEditEval(e)"
        >
          <div class="name">
            {{ evaluation.intern ? evaluation.intern.firstname + " " + evaluation.intern.lastname : "" }}
          </div>
        </generic-evals>
        <v-card
          v-if="canAddNewEval()"
          ripple
          class="ml-5"
          style="background: unset; height: fit-content"
          flat
        >
          <v-card-title primary-title>
            <v-avatar
              size="80"
              style="cursor: pointer"
              @click="newEval()"
            >
              <v-icon
                size="60"
                :class="{'circle-container': true, 'primary': true}"
                color="#ffffff"
              >
                mdi-plus
              </v-icon>
            </v-avatar>
          </v-card-title>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Consts from "@/consts"
import Sessions from '@/api/sessions'
import Users from '@/api/users'
import DateMixin from '@/mixins/date'
import Auth from '@/plugins/auth'
import GenericForm from "@/components/sessions/evals/GenericForm/Form.vue"
import GenericEvals from "@/components/sessions/evals/GenericEvalsList.vue"
import FormDataMixin from '@/mixins/formdata'

export default {
  components: { GenericForm, GenericEvals },
  mixins: [DateMixin, FormDataMixin],
  props: ['session', 'isAdmin', 'isCoord'],
  data () {
    return {
      evaluations: [],
      currentEval: null,
      user: null,
      fullValidated: false,
      loaded: false,
      formValid: false,
      isAdminEditingEval: false,
      evalToBeCreatedForInternID: null, //utilisé pour la création par l'admin
      Consts,
      editedIntern: null,
      clonedInternForEdition: null,
      editionOfInternData: false,
      hasNoForm: false
    }
  },
  computed: {
    isTrainer() {
      return !this.isAdmin && !this.isCoord
    },
    remainingInterns() {
      return this.session.visitors.filter(t => {
        return ! this.evaluations.find(e => e.user_id == t.id)
      }).map(t => {
        return {id: t.id, fullname: `${t.firstname} ${t.lastname}`}
      })
    },
    evaluationsByTime() {
      return this.evaluations.toSorted((a, b) => {
        const aComp = a.updated_at ?? a.created_at
        const bComp = b.updated_at ?? b.created_at

        if(aComp < bComp) {
          return -1
        }
        else if(aComp > bComp) {
          return 1
        }
        return 0
      })
    },
    isReadonly () {
      return this.currentEval.form_type != Consts.EVALUATIONS_TYPES.EVALUATION_INTERN && (this.session.status == Consts.TOCONTROL || this.session.status == Consts.CLOSED)
    }
  },
  watch: {
    working_time() {
      if(this.currentEval && this.working_time <= this.sessionTotalTime) {
        this.currentEval.working_time_exceeding_reason = ""
      }
    },
    async currentEval() { //Cyril : j'aime moyen ça mais c'est le seul moyen de couvrir tous les cas où currentEval change, y compris dans le futur, pour pas oublier de charger l'utilisateur dont on va pouvoir modifier les données personnelles
      if(this.currentEval) {
        const {data} = await Users.show(this.currentEval.intern.id)
        this.editedIntern = data
      }
      else {
        this.editedIntern = null
      }
    }
  },
  mounted () {
    this.user = Auth.user
    this.fetch()
  },
  methods: {
    async fetch () {
      /**
       * on doit tenir compte du profil de l'utilisateur
       * soit on est admin/coord alors on va tout chercher
       * soit on est trainer alors on va chercher (ou créer à la volée) l'évaluation de ce trainer
       */

      if(! this.isTrainer) {
        const {data: evaluations} = await Sessions.getInternsEvals(this.$route.params.id)
        this.evaluations = evaluations
        this.currentEval = null
      }
      else {
        //ici c'est particulier parce qu'en DB sur l'évaluation on a l'user_id de l'intern, pas de l'exepert responsable
        //on doit aller chercher sur la session si le mec connecté est expert et qu'il a un interne associé pour ne prendre que l'évaluation
        //de cet interne
        const intern = this.session.session_visitor.find(v => v.trainer_id == this.user.id) //il ne peut y en avoir que 0 ou 1

        if(intern) { //on rentrera ici que si le mec connecté a un visitor lié, donc pas de risque de créer des évaluations pour des trainers qui ne sont pas experts
          const {data: evaluation} = await Sessions.getInternsEvals(this.$route.params.id, {user_id: intern.user_id})
          this.currentEval = evaluation ? evaluation[0] : null
          if( ! this.currentEval) { //on la crée
            try {
              const {data: cEval} = await Sessions.createInternEval(this.session.id, {})
              this.currentEval = cEval
              this.evaluations.push(this.currentEval) //on met à jour la liste au cas où
            }
            catch(e) {
              if(e.response && e.response.status == 422) {
                this.hasNoForm = true
                this.currentEval = null
              }
              this.loaded = true
            }
          }
        }
        else {
          this.currentEval = null
        }
      }
      this.loaded = true
    },
    updateRapport(evaluation) {
      this.currentEval = evaluation
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$snotify.error('Please check the content of your form')
        return;
      }

      try {
        let cloneEval = structuredClone(this.currentEval)
        const {data} = await Sessions.updateInternEval(this.session.id, this.currentEval.id, cloneEval)
        cloneEval = null // mémoire
        if(!this.isAdminEditingEval) {
          this.currentEval = data
        }
        else {
          this.isAdminEditingEval = false
        }
        this.evaluations.splice(this.evaluations.indexOf(this.evaluations.find(e => e.id == this.currentEval.id)), 1, data)
        this.$snotify.success('The evaluation has been saved successfully')
      }
      catch(e) {
        //intercepted by axios
        console.log(e)
      }
    },
    adminEditEval(e) {
      this.isAdminEditingEval = true
      this.currentEval = e
    },
    canAddNewEval () {
      return this.isCoord || this.isAdmin
    },
    newEval() {
      this.currentEval = null
      this.isAdminEditingEval = true
      this.evalToBeCreatedForInternID = this.remainingInterns.length > 1  || this.remainingInterns.length == 0 ? null : this.remainingInterns[0].id
    },
    async createEval() {
      if( ! this.evalToBeCreatedForInternID) {
        this.$snotify.error('Please choose an intern')
        return
      }
      const {data} = await Sessions.createInternEval(this.session.id, {intern_id: this.evalToBeCreatedForInternID})
      this.currentEval = data
      this.isAdminEditingEval = true
      this.evaluations.push(data)
      this.evalToBeCreatedForInternID = null
      this.$snotify.success('Evaluation created with success')
    },
    cancelCreation() {
      this.evalToBeCreatedForInternID = null
      this.isAdminEditingEval = false
    }
  }
}
</script>

<style scoped lang="sass">
  .alert
    padding: 2em
    border-radius: 9px
    border: 2px solid tomato
    color: tomato
    width: 100%
    font-size: 1.2rem
    display: flex
    flex-direction: row
    align-items: center

    i.v-icon
      font-size: 60px
      color: tomato
      margin-right: 20px

  .legend
    border-left: 5px solid tomato
    padding-left: 10px
    color: #999999
    margin-top: 10px

    &.no-border
      border-left: 5px solid #C1C1C1

    &.info-legend
      border-left: 5px solid #5dade2
      padding: 10px 0px 10px 10px
      font-size: 1.2em

  form
    width: 100%

  .main-container
    width: 100%
    display: flex
    flex-direction: row
    align-items: top
    flex-wrap: wrap-reverse

    .form-container
      flex-grow: 1
      min-width: 500px
      background-color: white
      padding: 24px
      border-radius: 9px

    .meta-container
      width: 400px
      margin-left: 24px
      position: relative

      .meta
        position: sticky
        top: 0px
        padding-top: 20px


      .save-section
        display: flex
        flex-direction: row
        justify-content: center
        align-items: center

  .rating-container
    display: flex
    flex-direction: row
    align-items: center

    > a
      text-decoration: underline
      cursor: pointer
      margin-left: 20px

  #create-as-admin
    display: flex
    align-items: center
    justify-content: flex-start
    width: 30%

  .personal-data
    border-radius: 9px
    background-color: white
    padding: 10px

    .personal-header
      display: flex
      align-items: center

      h3
        flex-grow: 1

      .avatar i
        color: #f4b944
        margin-right: 10px

      a
        cursor: pointer
        display: flex
        width: 32px
        height: 32px
        border-radius: 50%
        justify-content: center
        align-items: center

        i
          color: #f4b944

        &:hover
          background-color: #F1F1F1
    .data
      display: flex
      flex-direction: row
      align-items: flex-start

      .sub-data
        margin: 5px

        .missing
          color: tomato
          font-style: italic

        td
          vertical-align: top

        td.label
          width: 70px

        &.sub-grow
          flex-grow: 1
          min-width: 120px

    h4
      color: #AAAAAA
      margin-bottom: 10px
      font-size: 1.1em

  .form-row
    display: flex
    flex-direction: row
    margin-bottom: 5px
    align-items: center

    .w50
      width: 50px
    .w75
      width: 75px
    .mr-1
      margin-right: 10px
    .grow
      flex-grow: 1

    &.center
      justify-content: center

  .flex-v-center
    display: flex
    flex-direction: row
    align-items: center

  .underlined
    text-decoration: underline
    cursor: pointer

  .remover
    cursor: pointer
    &:hover
      i
        color: tomato

  .mb-80
    margin-bottom: 80px
</style>
